import { useState, useEffect } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Package,
  Power,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "../../lib/toast";
import {
  server_post_data,
  get_product_page_data,
  manage_page_active_status,
  manage_page_delete,
} from "../../serviceconnection/ServiceConnection";
import { formatDateString } from "../../commonjquery/CommonJquery";

const isActive = (status) => status === 1 || status === "published";

const STATUS_CONFIG = {
  active: { label: "Published", className: "status-published" },
  inactive: { label: "Unpublished", className: "status-unpublished" },
};

function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = "Confirm",
  variant = "danger",
}) {
  if (!open) return null;

  const isDanger = variant === "danger";
  const isWarning = variant === "warning";

  const iconBg = isDanger ? "bg-red-500/10" : "bg-yellow-500/10";
  const iconColor = isDanger ? "text-red-500" : "text-yellow-500";
  const btnClass = isDanger
    ? "bg-red-500 hover:bg-red-600 text-white"
    : "bg-yellow-500 hover:bg-yellow-600 text-white";

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}
      onMouseDown={onCancel}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl border shadow-2xl p-6 space-y-5"
        style={{
          background: "hsl(var(--card))",
          borderColor: "hsl(var(--border))",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}
          >
            {isDanger ? (
              <Trash2 className={`w-5 h-5 ${iconColor}`} />
            ) : (
              <Power className={`w-5 h-5 ${iconColor}`} />
            )}
          </div>
          <div className="space-y-1 pt-0.5">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {description}
            </p>
          </div>
        </div>

        <div
          className="border-t"
          style={{ borderColor: "hsl(var(--border))" }}
        />

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-xs font-medium border hover:bg-muted transition-colors"
            style={{
              borderColor: "hsl(var(--border))",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${btnClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProductsList() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // dialog state: { type: "delete"|"toggle", localId, active? } | null
  const [dialog, setDialog] = useState(null);

  // ── Open helpers ──────────────────────────────────────────────────────────
  const confirmDelete = (id) => setDialog({ type: "delete", localId: id });
  const confirmToggle = (localId, currentlyActive) =>
    setDialog({ type: "toggle", localId, active: currentlyActive });

  // ── Confirmed actions ─────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!dialog) return;

    if (dialog.type === "delete") {
      const updated = products.filter((s) => s.id !== dialog.localId);
      try {
        const formData = new FormData();
        formData.append("id", dialog.localId);
        formData.append("pages_flags", "product");

        await server_post_data(manage_page_delete, formData);

        // const updated = solutions.map((s) => {
        //   if (s._localId !== dialog.localId) return s;
        //   return { ...s, publish_status: newStatus };
        // });
        setProducts(updated);
        showSuccess("Solution deleted");
        fetchProducts();
      } catch (err) {
        console.error("Failed to update status:", err);
        showError("Failed to update status");
      }
    }

    if (dialog.type === "toggle") {
      const newStatus = dialog.active ? 0 : 1; // 0 = deactive, 1 = active

      try {
        const formData = new FormData();
        formData.append("id", dialog.localId);
        formData.append("status", newStatus);
        formData.append("pages_flags", "product");

        await server_post_data(manage_page_active_status, formData);

        const updated = products.map((s) => {
          if (s._localId !== dialog.localId) return s;
          return { ...s, publish_status: newStatus };
        });
        setProducts(updated);
        showSuccess(
          dialog.active ? "Product unpublished" : "Product published",
        );
        fetchProducts();
      } catch (err) {
        console.error("Failed to update status:", err);
        showError("Failed to update status");
      }
    }

    setDialog(null);
  };

  // ── Dialog content by type ────────────────────────────────────────────────
  const dialogProps = dialog
    ? dialog.type === "delete"
      ? {
          variant: "danger",
          title: "Delete Solution",
          description:
            "This action is permanent and cannot be undone. The solution page will be removed from the CMS.",
          confirmLabel: "Yes, Delete",
        }
      : {
          variant: "warning",
          title: dialog.active ? "Deactivate Solution" : "Activate Solution",
          description: dialog.active
            ? "This will unpublish the solution page and hide it from the website. You can re-activate it anytime."
            : "This will publish the solution page and make it live on the website.",
          confirmLabel: dialog.active ? "Yes, Deactivate" : "Yes, Activate",
        }
    : {};

  const fetchProducts = async () => {
    try {
      setListLoading(true);
      const formData = new FormData();
      const response = await server_post_data(get_product_page_data, formData);
      setProducts(response.data?.data ?? []);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      showError("Failed to load products:", err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <>
      {/* ── Confirm Dialog ── */}
      <ConfirmDialog
        open={!!dialog}
        onConfirm={handleConfirm}
        onCancel={() => setDialog(null)}
        {...dialogProps}
      />

      <div className="max-w-[1400px] mx-auto space-y-5">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: "hsl(var(--accent) / 0.12)" }}
            >
              <Package
                className="w-5 h-5"
                style={{ color: "hsl(var(--accent))" }}
              />
            </div>
            <div>
              <h1 className="page-title">Products</h1>
              <p className="page-subtitle">
                SAP Business One, S/4HANA, Analytics Cloud & more
              </p>
            </div>
          </div>
          <button
            className="btn-accent"
            onClick={() => navigate("/products/new")}
          >
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>

        {/* Category counters */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          {[
            "SAP Bussiness One",
            "SAP Cloud ERP",
            "CRM & Analytics",
            "Integration & Automation",
          ].map((cat) => (
            <div key={cat} className="cms-card p-4">
              <p
                className="text-2xl font-bold"
                style={{ color: "hsl(var(--accent))" }}
              >
                {products.filter((p) => p.product_category === cat).length}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">By {cat}</p>
            </div>
          ))}
        </div>

        <div className="section-panel">
          <div className="overflow-x-auto">
            <table className="w-full cms-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const active = isActive(p.status);
                  const statusCfg = active
                    ? STATUS_CONFIG.active
                    : STATUS_CONFIG.inactive;
                  return (
                    <tr key={p.id} className="group">
                      <td className="font-semibold text-foreground">
                        {p.product_name}
                      </td>
                      <td>
                        <span
                          className="text-xs font-medium px-2.5 py-1 rounded-full"
                          style={{
                            background: "hsl(var(--info-muted))",
                            color: "hsl(var(--info))",
                          }}
                        >
                          {p.product_category}
                        </span>
                      </td>
                      <td>
                        <span className={statusCfg.className}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="text-muted-foreground">
                        {" "}
                        {formatDateString(p.entry_date)}
                      </td>
                      <td>
                        <div className="flex items-center gap-1 opacity-100 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => confirmToggle(p.id, active)}
                            title={active ? "Deactivate page" : "Activate page"}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
                            style={{
                              color: active
                                ? "hsl(var(--success, 142 71% 45%))"
                                : "hsl(var(--muted-foreground))",
                            }}
                          >
                            {active ? (
                              <ToggleRight className="w-5 h-5" />
                            ) : (
                              <ToggleLeft className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={() => navigate(`/products/${p.id}/edit`)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => confirmDelete(p.id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {products.length === 0 && (
            <div className="empty-state py-20">
              <div className="empty-state-icon">
                <Package className="w-6 h-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">
                No products yet
              </p>
              <button
                className="btn-accent mt-4"
                onClick={() => navigate("/products/new")}
              >
                <Plus className="w-4 h-4" /> Add First Product
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
